import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// 144 hours/year, 26 biweekly pay periods = 5.5385 hrs/period
const ANNUAL_HOURS = 144;
const PAY_PERIODS_PER_YEAR = 26;
const ACCRUAL_RATE = ANNUAL_HOURS / PAY_PERIODS_PER_YEAR; // ~5.54 hrs per period

const getPayPeriodsElapsed = () => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((now - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
  return Math.floor(dayOfYear / 14);
};

const HoursBar = ({ used, accrued, total }) => {
  const accruedPct = total > 0 ? Math.min((accrued / total) * 100, 100) : 0;
  const usedPct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(accrued - used, 0);
  return (
    <div className="space-y-1">
      <div className="relative h-2 bg-muted rounded-full overflow-hidden">
        <div className="absolute h-full bg-primary/20 rounded-full" style={{ width: `${accruedPct}%` }} />
        <div className="absolute h-full bg-primary rounded-full" style={{ width: `${usedPct}%` }} />
      </div>
      <div className="flex justify-between text-xs font-data text-muted-foreground">
        <span>{used?.toFixed(1)}h used</span>
        <span className="text-primary font-medium">{remaining?.toFixed(1)}h left</span>
      </div>
    </div>
  );
};

const EditBalanceModal = ({ employee, balance, onClose, onSave }) => {
  const [adjustHours, setAdjustHours] = useState('');
  const [adjustType, setAdjustType] = useState('add');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentAccrued = balance?.hoursAccrued ?? 0;
  const currentUsed = balance?.hoursUsed ?? 0;
  const currentAdjusted = balance?.hoursAdjusted ?? 0;
  const currentRemaining = Math.max(currentAccrued + currentAdjusted - currentUsed, 0);

  const handleSave = async () => {
    const hrs = parseFloat(adjustHours);
    if (!adjustHours || isNaN(hrs) || hrs <= 0) {
      setError('Please enter a valid positive number of hours.');
      return;
    }
    if (!reason?.trim()) {
      setError('Please provide a reason for this adjustment.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        balanceId: balance?.id,
        employeeId: employee?.id,
        hours: adjustType === 'add' ? hrs : -hrs,
        reason: reason?.trim(),
      });
      onClose();
    } catch (err) {
      setError('Failed to save adjustment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl elevation-lg w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-base font-heading font-semibold text-foreground">Edit PTO Balance</h2>
            <p className="text-xs font-caption text-muted-foreground mt-0.5">{employee?.name} · {employee?.department}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Current Balance Summary */}
          <div className="bg-muted/30 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs font-caption text-muted-foreground">Accrued</p>
              <p className="text-lg font-data font-bold text-foreground">{currentAccrued?.toFixed(1)}h</p>
            </div>
            <div>
              <p className="text-xs font-caption text-muted-foreground">Used</p>
              <p className="text-lg font-data font-bold text-warning">{currentUsed?.toFixed(1)}h</p>
            </div>
            <div>
              <p className="text-xs font-caption text-muted-foreground">Remaining</p>
              <p className={`text-lg font-data font-bold ${currentRemaining < 8 ? 'text-destructive' : 'text-success'}`}>
                {currentRemaining?.toFixed(1)}h
              </p>
            </div>
          </div>

          {/* Adjustment Type */}
          <div>
            <label className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Adjustment Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setAdjustType('add')}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body font-medium border transition-colors ${
                  adjustType === 'add' ?'bg-success/10 border-success text-success' :'bg-background border-border text-muted-foreground hover:border-success/50'
                }`}
              >
                <Icon name="Plus" size={15} />
                Add Hours
              </button>
              <button
                onClick={() => setAdjustType('subtract')}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body font-medium border transition-colors ${
                  adjustType === 'subtract' ?'bg-destructive/10 border-destructive text-destructive' :'bg-background border-border text-muted-foreground hover:border-destructive/50'
                }`}
              >
                <Icon name="Minus" size={15} />
                Subtract Hours
              </button>
            </div>
          </div>

          {/* Hours Input */}
          <div>
            <label className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Hours</label>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={adjustHours}
              onChange={(e) => { setAdjustHours(e?.target?.value); setError(''); }}
              placeholder="e.g. 8"
              className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
            {adjustHours && !isNaN(parseFloat(adjustHours)) && (
              <p className="text-xs text-muted-foreground mt-1">
                New remaining: <span className="font-semibold text-foreground">
                  {Math.max(currentAccrued + currentAdjusted - currentUsed + (adjustType === 'add' ? parseFloat(adjustHours) : -parseFloat(adjustHours)), 0)?.toFixed(1)}h
                </span>
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => { setReason(e?.target?.value); setError(''); }}
              placeholder="e.g. Correction for missed accrual, bonus hours, etc."
              rows={2}
              className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 pb-5">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="default"
            size="sm"
            iconName={saving ? 'Loader2' : 'Save'}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Adjustment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const PTOBalanceTable = ({ employees, balances, loading, onEditBalance }) => {
  const [editingEmployee, setEditingEmployee] = useState(null);

  const getBalance = (employeeId) =>
    balances?.find((b) => b?.employeeId === employeeId);

  const payPeriodsElapsed = getPayPeriodsElapsed();
  const defaultAccrued = Math.min(payPeriodsElapsed * ACCRUAL_RATE, ANNUAL_HOURS);

  if (loading) {
    return (
      <div className="bg-card rounded-xl elevation-sm overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-sm font-heading font-semibold text-foreground">PTO Balances</h3>
        </div>
        <div className="p-8 text-center">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading balances...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-xl elevation-sm overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground">Employee PTO Balances</h3>
            <p className="text-xs font-caption text-muted-foreground mt-0.5">
              {ANNUAL_HOURS}h/year · Accrues {ACCRUAL_RATE?.toFixed(2)}h every 2-week pay period · {payPeriodsElapsed} of {PAY_PERIODS_PER_YEAR} periods elapsed
            </p>
          </div>
          <span className="text-xs font-caption text-muted-foreground">{new Date()?.getFullYear()} Year</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Employee</th>
                <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Department</th>
                <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground min-w-[160px]">Balance Progress</th>
                <th className="text-center px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Accrued</th>
                <th className="text-center px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Used</th>
                <th className="text-center px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Adjusted</th>
                <th className="text-right px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Remaining</th>
                <th className="text-right px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-sm text-muted-foreground">
                    No employees found
                  </td>
                </tr>
              ) : (
                employees?.map((emp) => {
                  const bal = getBalance(emp?.id);
                  const accrued = bal?.hoursAccrued ?? defaultAccrued;
                  const used = bal?.hoursUsed ?? 0;
                  const adjusted = bal?.hoursAdjusted ?? 0;
                  const remaining = Math.max(accrued + adjusted - used, 0);

                  return (
                    <tr key={emp?.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-body font-medium text-foreground">{emp?.name}</p>
                          <p className="text-xs font-caption text-muted-foreground">{emp?.employeeId}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-body text-muted-foreground">{emp?.department ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3 min-w-[160px]">
                        <HoursBar used={used} accrued={accrued + adjusted} total={ANNUAL_HOURS} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-data text-primary font-semibold">{accrued?.toFixed(1)}h</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-data text-warning font-semibold">{used?.toFixed(1)}h</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-data font-semibold ${
                          adjusted > 0 ? 'text-success' : adjusted < 0 ? 'text-destructive' : 'text-muted-foreground'
                        }`}>
                          {adjusted > 0 ? '+' : ''}{adjusted?.toFixed(1)}h
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-data font-semibold ${
                          remaining < 8 ? 'text-destructive' :
                          remaining < 24 ? 'text-warning' : 'text-success'
                        }`}>
                          {remaining?.toFixed(1)}h
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setEditingEmployee(emp)}
                          className="p-1.5 rounded-lg bg-muted hover:bg-primary/10 hover:text-primary text-muted-foreground transition-colors"
                          title="Edit balance"
                        >
                          <Icon name="Pencil" size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {editingEmployee && (
        <EditBalanceModal
          employee={editingEmployee}
          balance={getBalance(editingEmployee?.id)}
          onClose={() => setEditingEmployee(null)}
          onSave={onEditBalance}
        />
      )}
    </>
  );
};

export default PTOBalanceTable;
